import type { Field } from "../Field";
import { initField } from "../Field";
import type { GenericSchema, InferOutput } from "valibot";
import {
  array,
  arrayAsync,
  boolean,
  check,
  fallback,
  intersect,
  intersectAsync,
  literal,
  minLength,
  nullish,
  object,
  objectAsync,
  pipe,
  safeParse,
  string,
  variant,
  variantAsync,
} from "valibot";
import {
  dataName,
  errorTranslation,
  label,
  optionalTextValue,
  requireResource,
  requireText,
  resource,
  uniqueItems,
} from "../../utils/validation/base";
import uuid from "../../utils/uuid";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { DECORATIVE_IMAGE_ALT, MAX_CHARACTERS_MD, MAX_CHARACTERS_SM, PREDEFINED_CONDITION_KEYS } from "../../constants";
import { useFormServices } from "../../services/formServices";
import { isEmpty, isNil } from "lodash-es";
import { createEffect, createResource, getOwner, on, runWithOwner, Show } from "solid-js";
import CustomerResourceService from "../../services/CustomerResourceService";
import { resourceAsync } from "../../utils/validation/async";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import Image from "../valueRendering/Image";
import type { SelectOption } from "../../types/Menu";
import { SingleSelectInput } from "../../components/menu/Select";
import { PreviewContentLabel, RadioOptionsPreview } from "../FieldPreviewComponents";
import { Toggle } from "../../components/toggle/Toggle";
import PropertySection from "../PropertySection";
import ToggleButtonGroup from "../../components/toggle/ToggleButtonGroup";
import { OptionsPropertyInput } from "../OptionsPropertyInput";
import { keyFromSelector } from "i18next";

const DtoSchema = object({
  label_text: string(),
  data_name: string(),
  use_images_as_labels: fallback(boolean(), false),
  radio_options: array(
    object({
      id: nullish(string()),
      name: nullish(string()),
      imgResource: nullish(string()),
      email_label: nullish(string()),
    }),
  ),
  default_value: nullish(string()),
  vertical_alignment: fallback(boolean(), false),
  required: fallback(boolean(), false),
  remember_input: fallback(boolean(), false),
});

const radioOptions = (requireImage: boolean) =>
  pipe(
    array(radioOption(requireImage)),
    minLength(1, errorTranslation(keyFromSelector(($) => $.FIELD_PROPERTY_OPTIONS_MIN))),
    check(uniqueItems("id"), errorTranslation(keyFromSelector(($) => $.FIELD_PROPERTY_OPTIONS_DUPLICATE_VALUE))),
  );

const radioOption = (requireImage: boolean) =>
  object({
    key: string(), // Unchanging identifier for platform
    id: requireText(MAX_CHARACTERS_SM), // Value
    name: requireText(MAX_CHARACTERS_SM), // Label
    imgResource: requireImage ? requireResource() : resource(), // Image label
    email_label: optionalTextValue(MAX_CHARACTERS_MD), // (Value in) PDF
  });

const ResourceSchema = intersectAsync([
  variantAsync("use_images_as_labels", [
    object({
      use_images_as_labels: literal(false),
    }),
    objectAsync({
      use_images_as_labels: literal(true),
      radio_options: arrayAsync(
        objectAsync({
          imgResource: resourceAsync(),
        }),
      ),
    }),
  ]),
]);

const DomainSchema = intersect([
  object({
    label_text: label(),
    data_name: dataName(),
    use_images_as_labels: boolean(),
    default_value: nullish(string()),
    vertical_alignment: boolean(),
    required: boolean(),
    remember_input: boolean(),
  }),
  variant("use_images_as_labels", [
    object({
      use_images_as_labels: literal(false),
      radio_options: radioOptions(false),
    }),
    object({
      use_images_as_labels: literal(true),
      radio_options: radioOptions(true),
    }),
  ]),
]) satisfies GenericSchema<InferOutput<typeof ResourceSchema>>;

const ValueSchema = string();

export type RadioFieldProperties = InferOutput<typeof DomainSchema>;
type RadioOptionsSource = { radioOptions: RadioFieldProperties["radio_options"]; useImagesAsLabels: boolean };
export type RadioFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;

const RadioField: Field<RadioFieldProperties, RadioFieldDtoType, ValueType> = {
  type: "radio",
  category: "choices",
  widget: "com.moreapps:radio:1",
  schema: DomainSchema,
  resourceSchema: ResourceSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    radio_options: [
      { key: uuid(), id: "Option 1", name: "Option 1", imgResource: undefined },
      { key: uuid(), id: "Option 2", name: "Option 2", imgResource: undefined },
    ],
    use_images_as_labels: false,
    default_value: null,
    vertical_alignment: false,
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { variant: "select", keys: PREDEFINED_CONDITION_KEYS },
    actions: { variant: "select" },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: (value) => value,
  renderValue: (value, { fieldConfig }) => {
    const { t } = useTranslation();
    const option = fieldConfig.properties.radio_options.find((option) => option.id === value);

    return (
      <Show when={option} fallback={value}>
        {(v) =>
          fieldConfig.properties.use_images_as_labels ? (
            <Image
              fileId={v().imgResource!}
              scope="customer"
              ariaLabel={t(($) => $.RADIO_FIELD_VALUE_IMAGE_ARIA_LABEL)}
              modalTitle={fieldConfig.properties.label_text}
              modalIcon={RadioField.icon}
              modalCategory={RadioField.category}
            />
          ) : (
            v().name
          )
        }
      </Show>
    );
  },
  icon: "checkCircle",
  titleKey: keyFromSelector(($) => $.RADIO_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.RADIO_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    radio_options: keyFromSelector(($) => $.FIELD_PROPERTY_OPTIONS_LABEL),
    default_value: keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_VALUE),
    vertical_alignment: keyFromSelector(($) => $.FIELD_PROPERTY_VERTICAL_ALIGNMENT),
    use_images_as_labels: keyFromSelector(($) => $.FIELD_PROPERTY_USE_IMAGES_AS_LABEL),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  fromDto: (dto) => {
    const radioOptions = dto.radio_options
      .filter((option) => !isNil(option.id))
      .map((option) => ({
        key: uuid(),
        id: option.id,
        name: dto.use_images_as_labels ? option.id : option.name, // Fallback to 'id' since we expect a value
        imgResource: dto.use_images_as_labels ? option.imgResource : undefined,
        email_label: option.email_label,
      }));
    const { output } = safeParse(DomainSchema, {
      ...dto,
      radio_options: radioOptions,
      default_value: radioOptions.find((option) => option.id === dto.default_value)?.key ?? null,
    });
    return output as RadioFieldProperties;
  },
  toDto: (domain) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      radio_options: domain.radio_options.map((option) => ({
        id: option.id,
        name: domain.use_images_as_labels ? undefined : option.name,
        imgResource: domain.use_images_as_labels ? option.imgResource : undefined,
        email_label: option.email_label,
      })),
      default_value: domain.radio_options.find((option) => option.key === domain.default_value)?.id ?? null,
    });
    return output as RadioFieldDtoType;
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <Show when={!isEmpty(props.properties.radio_options)}>
        <RadioOptionsPreview
          radio_options={props.properties.radio_options}
          vertical_alignment={props.properties.vertical_alignment}
          default_value={props.properties.default_value}
          use_images_as_labels={props.properties.use_images_as_labels}
        />
      </Show>
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, currentScope } = useFormServices();
    const owner = getOwner();
    const [options] = createResource<SelectOption<string>[], RadioOptionsSource>(
      () => ({
        radioOptions: props.data.properties.radio_options,
        useImagesAsLabels: props.data.properties.use_images_as_labels,
      }),
      async ({ radioOptions, useImagesAsLabels }) => {
        if (isNil(radioOptions)) {
          return [];
        }
        if (!useImagesAsLabels) {
          return radioOptions.map((option) => ({
            variant: "text",
            label: option.name,
            value: option.key,
          }));
        }
        return Promise.all(
          radioOptions.map(async ({ id, key, name, imgResource }) => {
            if (isNil(imgResource)) {
              return { variant: "text", label: name, value: key };
            }

            const uri = await runWithOwner(owner, async () => CustomerResourceService.getSecureUri(imgResource));
            if (isNil(uri)) {
              return { variant: "text", label: name, value: key };
            }
            return { variant: "image", label: id, value: key, image: { src: uri, alt: DECORATIVE_IMAGE_ALT } };
          }),
        );
      },
    );

    createEffect(
      on(
        () => ({
          defaultValue: props.data.properties.default_value,
          keys: props.data.properties.radio_options.map((option) => option.key),
        }),
        ({ defaultValue, keys }) => {
          // Set `default_value` to null when it is not an option anymore
          if (!isNil(defaultValue) && !keys.includes(defaultValue)) {
            props.setValue("default_value", null);
          }
        },
        { defer: true },
      ),
    );

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_DISPLAY)}>
          <div class="flex flex-col gap-y-2">
            <ToggleButtonGroup
              label={t(props.propertyKeys.use_images_as_labels)}
              value={props.data.properties.use_images_as_labels ? "true" : "false"}
              onValueChange={(details) => props.setValue("use_images_as_labels", details.value === "true")}
              items={[
                { icon: "textIcon", value: "false", ariaLabel: t(($) => $.FIELD_PROPERTY_RADIO_TEXT_ARIA_LABEL) },
                { icon: "image", value: "true", ariaLabel: t(($) => $.FIELD_PROPERTY_RADIO_IMAGE_ARIA_LABEL) },
              ]}
            />
            <ToggleButtonGroup
              label={t(props.propertyKeys.vertical_alignment)}
              value={props.data.properties.vertical_alignment ? "true" : "false"}
              onValueChange={(details) => props.setValue("vertical_alignment", details.value === "true")}
              items={[
                {
                  icon: "horizontal",
                  value: "false",
                  ariaLabel: t(($) => $.FIELD_PROPERTY_RADIO_HORIZONTAL_ARIA_LABEL),
                },
                { icon: "vertical", value: "true", ariaLabel: t(($) => $.FIELD_PROPERTY_RADIO_VERTICAL_ARIA_LABEL) },
              ]}
            />
          </div>

          <OptionsPropertyInput
            options={props.data.properties.radio_options}
            onChange={(updated) => props.setValue("radio_options", updated)}
            variant={props.data.properties.use_images_as_labels ? "image" : "text"}
            error={props.getError("radio_options")}
            nestedErrors={props.getNestedErrors("radio_options")}
            currentScope={currentScope}
          />

          <SingleSelectInput
            label={t(props.propertyKeys.default_value)}
            value={props.data.properties.default_value}
            onSelect={(value) => props.setValue("default_value", value)}
            onClear={() => props.setValue("default_value", undefined)}
            options={options() ?? []}
            errorMessage={props.getError("default_value")}
            sameWidth
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(RadioField);
